import React, { useMemo } from 'react';
import useMarkdown from '../../hooks/useMarkdown';
import { useTranslation } from '../../hooks/useTranslation';
import remarkGfm from 'remark-gfm';

// Add styles to prevent unwanted paragraph margins in list items
const listItemStyles = `
  .markdown-list-item > p {
    margin: 0 !important;
    display: inline !important;
    padding: 0 !important;
  }
  
  .markdown-list-item > p:first-child {
    display: inline !important;
  }
  
  .markdown-list-item > p:last-child {
    margin-bottom: 0 !important;
  }
  
  .markdown-list-item {
    margin-bottom: 0.25rem;
    line-height: 1.5;
  }
  
  .markdown-list-item:last-child {
    margin-bottom: 0;
  }
  
  /* Handle any other inline elements within list items */
  .markdown-list-item > strong,
  .markdown-list-item > em,
  .markdown-list-item > code,
  .markdown-list-item > a {
    display: inline;
    margin: 0;
    padding: 0;
  }
  
  /* Ensure proper spacing for nested lists (both ul and ol) */
  .markdown-list-item ul,
  .markdown-list-item ol {
    margin-top: 0.25rem;
    margin-bottom: 0;
  }
  
  /* Additional styling for ordered lists to ensure consistency */
  ol .markdown-list-item {
    margin-bottom: 0.25rem;
    line-height: 1.5;
  }
  
  ol .markdown-list-item:last-child {
    margin-bottom: 0;
  }
  
  ol .markdown-list-item > p {
    margin: 0 !important;
    display: inline !important;
    padding: 0 !important;
  }
  
  /* Table styling */
  .markdown-table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    font-size: 0.95rem;
  }
  
  .markdown-table th {
    background: linear-gradient(135deg, #1a3a52, #2d5f7e);
    color: #ffffff;
    font-weight: 600;
    padding: 12px 16px;
    text-align: left;
    border-bottom: 2px solid #2d5f7e;
    line-height: 1.3;
  }
  
  .markdown-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    color: #2c3e50;
    line-height: 1.3;
  }
  
  .markdown-table tbody tr:hover {
    background: rgba(45, 95, 126, 0.05);
  }
  
  .markdown-table tbody tr:last-child td {
    border-bottom: none;
  }
  
  .markdown-table code {
    background: rgba(212, 165, 116, 0.15);
    color: #c08f5c;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.9em;
  }
  
  .markdown-table strong {
    color: #1a3a52;
    font-weight: 600;
  }
`;

/**
 * Custom remark plugin to unwrap p tags from list items
 * This prevents unwanted paragraph wrapping within list items (both ul and ol)
 */
const unwrapParagraphsFromLists = () => {
  return (tree) => {
    const visit = (node) => {
      if (node?.type === 'listItem') {
        // Find and unwrap any paragraph nodes within list items
        if (node?.children && Array.isArray(node?.children)) {
          node.children = node.children.map(child => {
            if (child?.type === 'paragraph') {
              // Return the paragraph's children directly, removing the p wrapper
              return child.children;
            }
            return child;
          }).flat(); // Flatten the array since we're removing the paragraph wrapper
        }
      }
      
      // Recursively visit all children
      if (node?.children) {
        node.children.forEach(visit);
      }
    };
    
    visit(tree);
  };
};

/**
 * Component for rendering markdown content with proper styling
 */
const MarkdownRenderer = ({ 
  content, 
  className = '', 
  showFullText = false, 
  maxLength = 400,
  onToggleText,
  showToggleButton = true,
  showMoreText,
  showLessText
}) => {
  const t = useTranslation();
  
  // Use provided translation props or fall back to translation hook
  const showMore = showMoreText || t('assessmentForm.showMore', 'Show More');
  const showLess = showLessText || t('assessmentForm.showLess', 'Show Less');
  // Helper function to truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Determine what content to render
  const textToRender = showFullText ? content : truncateText(content, maxLength);
  
  // Memoize components to prevent unnecessary re-renders
  const memoizedComponents = useMemo(() => ({
    h1: ({ children }) => (
      <h1 style={{
        fontSize: '1.5rem',
        fontWeight: '600',
        color: '#2c3e50',
        marginBottom: '0.5rem',
        marginTop: '1rem'
      }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#34495e',
        marginBottom: '0.5rem',
        marginTop: '0.75rem'
      }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#7f8c8d',
        marginBottom: '0.5rem',
        marginTop: '0.5rem'
      }}>
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p style={{
        lineHeight: '1.6',
        marginBottom: '0.75rem',
        color: '#2c3e50',
        fontSize: '0.95rem'
      }}>
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul style={{
        marginBottom: '0.75rem',
        paddingLeft: '1.5rem',
        color: '#2c3e50'
      }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol style={{
        marginBottom: '0.75rem',
        paddingLeft: '1.5rem',
        color: '#2c3e50'
      }}>
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="markdown-list-item">
        {children}
      </li>
    ),
    strong: ({ children }) => (
      <strong style={{
        fontWeight: '600',
        color: '#2c3e50'
      }}>
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em style={{
        fontStyle: 'italic',
        color: '#34495e'
      }}>
        {children}
      </em>
    ),
    code: ({ children, className }) => {
      const isCodeBlock = className && className.startsWith('language-');
      if (isCodeBlock) {
        return (
          <pre style={{
            background: '#f8f9fa',
            padding: '0.75rem',
            borderRadius: '4px',
            overflow: 'auto',
            border: '1px solid #e9ecef',
            marginBottom: '0.75rem',
            fontSize: '0.875rem'
          }}>
            <code style={{ color: '#e83e8c' }}>{children}</code>
          </pre>
        );
      }
      return (
        <code style={{
          background: '#f8f9fa',
          padding: '0.2rem 0.4rem',
          borderRadius: '3px',
          color: '#e83e8c',
          fontSize: '0.875rem',
          fontFamily: 'monospace'
        }}>
          {children}
        </code>
      );
    },
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '3px solid #3498db',
        paddingLeft: '0.75rem',
        margin: '0.75rem 0',
        fontStyle: 'italic',
        color: '#7f8c8d',
        background: 'rgba(52, 152, 219, 0.05)',
        padding: '0.5rem 0.75rem',
        borderRadius: '0 4px 4px 0'
      }}>
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => (
      <a 
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#3498db',
          textDecoration: 'none',
          borderBottom: '1px solid transparent',
          transition: 'border-color 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.borderBottomColor = '#3498db'}
        onMouseLeave={(e) => e.target.style.borderBottomColor = 'transparent'}
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table className="markdown-table">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead>{children}</thead>
    ),
    tbody: ({ children }) => (
      <tbody>{children}</tbody>
    ),
    tr: ({ children }) => (
      <tr>{children}</tr>
    ),
    th: ({ children }) => (
      <th>{children}</th>
    ),
    td: ({ children }) => (
      <td>{children}</td>
    )
  }), []);

  // Memoize the remark plugins to prevent recreation on every render
  const memoizedRemarkPlugin = useMemo(() => unwrapParagraphsFromLists(), []);

  // Memoize the options object to prevent infinite re-renders
  const markdownOptions = useMemo(() => ({
    className: `markdown-content ${className}`,
    components: memoizedComponents,
    remarkPlugins: [remarkGfm, memoizedRemarkPlugin]
  }), [className, memoizedComponents, memoizedRemarkPlugin]);

  const { 
    renderedContent, 
    isLoading, 
    error 
  } = useMarkdown(textToRender, markdownOptions);

  if (isLoading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '1rem',
        color: '#6c757d',
        fontSize: '0.875rem'
      }}>
        Processing content...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '0.75rem',
        background: '#f8d7da',
        color: '#721c24',
        borderRadius: '4px',
        border: '1px solid #f5c6cb',
        fontSize: '0.875rem'
      }}>
        <strong>Error rendering content:</strong> {error}
      </div>
    );
  }

  return (
    <div className="markdown-renderer">
      <style>{listItemStyles}</style>
      <div className="markdown-content-wrapper">
        {renderedContent}
      </div>
      
      {showToggleButton && content && content.length > maxLength && onToggleText && (
        <button 
          className="toggle-text-btn"
          onClick={onToggleText}
          style={{
            background: 'none',
            border: 'none',
            color: '#3498db',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            padding: '0.5rem 0',
            marginTop: '0.5rem',
            textDecoration: 'underline'
          }}
          onMouseEnter={(e) => e.target.style.color = '#2980b9'}
          onMouseLeave={(e) => e.target.style.color = '#3498db'}
        >
          {showFullText ? showLess : showMore}
        </button>
      )}
    </div>
  );
};

export default MarkdownRenderer; 